import  { useState, useEffect } from 'react';
import { Plus, Trash2, Save, X, CreditCard, UserPlus, Eye, Globe, Copy } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Invoice, InvoiceItem } from '../types';
import { InvoiceTemplate } from './invoices/InvoiceTemplate';
import toast from 'react-hot-toast';
import { SEO } from './shared/SEO';

interface InvoiceFormProps {
  invoice?: Invoice | null;
  onSave: () => void;
  onCancel: () => void;
}

interface InvoiceFormData {
  clientId: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'cancelled';
  notes: string;
  terms: string;
  taxRate: number;
  discountAmount: number;
  includePaymentLink: boolean;
  paymentGateway?: string;
  customPaymentLink?: string;
  currency: string;
  signature?: string;
}

const SUPPORTED_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'QAR' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'AED' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'SAR' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'MAD' },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'TND' },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'DZD' },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'L£' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'JD' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'KD' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BD' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'OMR' },
];

// Add template options at the top
const TEMPLATE_OPTIONS: { value: string; label: string }[] = [
  { value: 'default', label: 'Classic' },
  { value: 'modern', label: 'Modern' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'bold', label: 'Bold' },
  { value: 'elegant', label: 'Elegant' },
];

export function InvoiceForm({ invoice, onSave }: InvoiceFormProps) {
  const { clients, createInvoice, updateInvoice, addClient, getNextInvoiceNumber } = useApp();
  const { currentWorkspace } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<InvoiceFormData>({
    clientId: '',
    invoiceNumber: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'draft',
    notes: '',
    terms: '',
    taxRate: 0,
    discountAmount: 0,
    includePaymentLink: false,
    paymentGateway: 'custom',
    customPaymentLink: '',
    currency: 'USD',
    signature: '',
  });

  const [items, setItems] = useState<InvoiceItem[]>([
    { id: '1', description: '', quantity: 1, rate: 0, amount: 0, taxable: true }
  ]);

  const [showClientModal, setShowClientModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [newClientData, setNewClientData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    taxId: '',
    contactPerson: '',
    notes: ''
  });

  const [selectedTemplate, setSelectedTemplate] = useState('default');

  const isEdit = !!invoice;

  useEffect(() => {
    if (invoice) {
      setFormData({
        clientId: invoice.clientId,
        invoiceNumber: invoice.invoiceNumber,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        status: invoice.status,
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        taxRate: invoice.taxRate,
        discountAmount: invoice.discountAmount,
        includePaymentLink: !!invoice.paymentLink,
        paymentGateway: (invoice as any).paymentGateway || 'custom',
        customPaymentLink: invoice.paymentLink || '',
        currency: invoice.currency || currentWorkspace?.settings.currency || 'QAR',
        signature: invoice.signature || '',
      });
      setItems(invoice.items);
    } else {
      const nextInvoiceNumber = getNextInvoiceNumber();
      setFormData(prev => ({ ...prev, invoiceNumber: nextInvoiceNumber }));
    }
  }, [invoice, getNextInvoiceNumber, currentWorkspace]);

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      taxable: true
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number | boolean) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'rate') {
          updated.amount = updated.quantity * updated.rate;
        }
        return updated;
      }
      return item;
    }));
  };

  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxableAmount = items.filter(item => item.taxable).reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = taxableAmount * (formData.taxRate / 100);
  const total = subtotal + taxAmount - formData.discountAmount;

  const getCurrencySymbol = (curr: string): string => {
    const currency = SUPPORTED_CURRENCIES.find(c => c.code === curr);
    return currency ? currency.symbol : curr;
  };

  const handleCreateClient = (e: React.FormEvent) => {
    e.preventDefault();
    addClient(newClientData);
    setNewClientData({
      name: '',
      email: '',
      address: '',
      phone: '',
      taxId: '',
      contactPerson: '',
      notes: ''
    });
    setShowClientModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Field validation
    type ErrorKey = 'clientId' | 'invoiceNumber' | 'issueDate' | 'dueDate' | 'items' | 'customPaymentLink';
    let firstError: ErrorKey | null = null;
    if (!formData.clientId) {
      toast.error('Please select a client');
      firstError = 'clientId';
    } else if (!formData.invoiceNumber) {
      toast.error('Invoice number is required');
      firstError = 'invoiceNumber';
    } else if (!formData.issueDate) {
      toast.error('Issue date is required');
      firstError = 'issueDate';
    } else if (!formData.dueDate) {
      toast.error('Due date is required');
      firstError = 'dueDate';
    } else if (items.length === 0 || items.every(item => !item.description.trim())) {
      toast.error('At least one line item with a description is required');
      firstError = 'items';
    } else if (formData.includePaymentLink && (!formData.customPaymentLink || !/^https?:\/\//.test(formData.customPaymentLink))) {
      toast.error('A valid payment link is required');
      firstError = 'customPaymentLink';
    }
    if (firstError) {
      // Optionally, focus the first invalid field
      const fieldMap: Record<ErrorKey, () => void> = {
        clientId: () => (document.querySelector('select[name="clientId"]') as HTMLElement)?.focus(),
        invoiceNumber: () => (document.querySelector('input[type="text"]') as HTMLElement)?.focus(),
        issueDate: () => (document.querySelector('input[type="date"]') as HTMLElement)?.focus(),
        dueDate: () => (document.querySelectorAll('input[type="date"]')[1] as HTMLElement)?.focus(),
        items: () => (document.querySelector('textarea[placeholder="Item description"]') as HTMLElement)?.focus(),
        customPaymentLink: () => (document.querySelector('input[type="url"]') as HTMLElement)?.focus(),
      };
      fieldMap[firstError]();
      return;
    }
    
    const selectedClient = clients.find(c => c.id === formData.clientId);
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    const invoiceData = {
      clientId: formData.clientId,
      client: selectedClient,
      invoiceNumber: formData.invoiceNumber,
      issueDate: formData.issueDate,
      dueDate: formData.dueDate,
      status: formData.status,
      items: items.filter(item => item.description.trim() !== ''),
      subtotal,
      taxRate: formData.taxRate,
      taxAmount,
      discountAmount: formData.discountAmount,
      total,
      notes: formData.notes,
      terms: formData.terms,
      paymentLink: formData.customPaymentLink || undefined,
      paymentStatus: 'pending' as const,
      currency: formData.currency,
      signature: formData.signature,
      template: selectedTemplate,
    };

    try {
      if (invoice) {
        await updateInvoice(invoice.id, invoiceData);
        toast.success('Invoice updated successfully!');
        onSave();
        navigate(`/app/invoices/${invoice.id}`); // Go to view page after update
      } else {
        const newInvoice = await createInvoice(invoiceData);
        toast.success('Invoice created successfully!');
        onSave();
        // If createInvoice returns the new invoice object or ID, use it:
        // Fix: handle case where newInvoice may not have an 'id' property
        const newInvoiceId = typeof newInvoice === 'object' && newInvoice !== null && 'id' in newInvoice
          ? (newInvoice as { id: string | number }).id
          : newInvoice;
        navigate(`/app/invoices/${newInvoiceId}`); // Go to view page after create
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice. Please try again.');
    }
  };

  const handleDuplicateInvoice = async () => {
    const selectedClient = clients.find(c => c.id === formData.clientId);
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    // Generate a new invoice number for the duplicate
    const nextInvoiceNumber = getNextInvoiceNumber();

    const duplicateInvoiceData = {
      clientId: formData.clientId,
      client: selectedClient,
      invoiceNumber: nextInvoiceNumber,
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'draft' as const,
      items: items.filter(item => item.description.trim() !== ''),
      subtotal,
      taxRate: formData.taxRate,
      taxAmount,
      discountAmount: formData.discountAmount,
      total,
      notes: formData.notes,
      terms: formData.terms,
      paymentLink: formData.customPaymentLink || undefined,
      paymentStatus: 'pending' as const,
      currency: formData.currency,
      signature: formData.signature,
      template: selectedTemplate,
    };

    try {
      await createInvoice(duplicateInvoiceData);
      toast.success('Invoice duplicated successfully!');
      navigate('/app/invoices');
    } catch (error) {
      console.error('Error duplicating invoice:', error);
      toast.error('Failed to duplicate invoice. Please try again.');
    }
  };

  // Create preview invoice for live preview
  const previewInvoice = formData.clientId ? {
    id: 'preview',
    workspaceId: 'preview',
    clientId: formData.clientId,
    client: clients.find(c => c.id === formData.clientId)!,
    invoiceNumber: formData.invoiceNumber,
    issueDate: formData.issueDate,
    dueDate: formData.dueDate,
    status: formData.status,
    items: items.filter(item => item.description.trim() !== ''),
    subtotal,
    taxRate: formData.taxRate,
    taxAmount,
    discountAmount: formData.discountAmount,
    total,
    notes: formData.notes,
    terms: formData.terms,
    paymentLink: formData.includePaymentLink ? formData.customPaymentLink : undefined,
    paymentStatus: 'pending' as const,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    currency: formData.currency,
    template: selectedTemplate,
    signature: formData.signature,
  } as Invoice : null;

  return (
    <>
      <SEO
        title={isEdit ? "Edit Invoice" : "New Invoice"}
        description={isEdit ? "Edit an existing invoice in FreelancePay." : "Create a new invoice for your freelance business with FreelancePay."}
        keywords={["invoice", isEdit ? "edit invoice" : "new invoice", "freelance", "FreelancePay"]}
      />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {invoice ? 'Edit Invoice' : 'Create New Invoice'}
              </h2>
              <p className="text-gray-600">
                {invoice ? 'Update invoice details' : 'Fill in the details below'}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Eye className="h-4 w-4 mr-2" />
                {showPreview ? 'Hide Preview' : 'Live Preview'}
              </button>
              <button
                onClick={() => navigate('/app/invoices')}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className={`grid ${showPreview ? 'grid-cols-2' : 'grid-cols-1'} gap-8`}>
            {/* Form Section */}
            <div className="space-y-6">
              <div onSubmit={handleSubmit} className="space-y-6">
                {/* Invoice Details */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Client *
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={formData.clientId}
                          onChange={(e) => setFormData(prev => ({ ...prev, clientId: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          required
                        >
                          <option value="">Select a client</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>{client.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowClientModal(true)}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                          title="Add New Client"
                        >
                          <UserPlus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Invoice Number *
                      </label>
                      <input
                        type="text"
                        value={formData.invoiceNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Issue Date *
                      </label>
                      <input
                        type="date"
                        value={formData.issueDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Currency
                      </label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <select
                          value={formData.currency}
                          onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          {SUPPORTED_CURRENCIES.map((currency) => (
                            <option key={currency.code} value={currency.code}>
                              {currency.code} - {currency.name} ({currency.symbol})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as Invoice['status'] }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tax Rate (%)
                      </label>
                      <input
                        type="number"
                        value={formData.taxRate}
                        onChange={(e) => setFormData(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Invoice Template
                      </label>
                      <select
                        value={selectedTemplate}
                        onChange={e => {
                          setSelectedTemplate(e.target.value);
                          if (!showPreview) setShowPreview(true);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        {TEMPLATE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Payment Options */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Payment Options</h4>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="includePaymentLink"
                          checked={formData.includePaymentLink}
                          onChange={(e) => setFormData(prev => ({ ...prev, includePaymentLink: e.target.checked }))}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label htmlFor="includePaymentLink" className="ml-2 flex items-center text-sm text-gray-700">
                          <CreditCard className="h-4 w-4 mr-1" />
                          Include payment link
                        </label>
                      </div>

                      {formData.includePaymentLink && (
                        <div className="ml-6 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Payment Gateway
                            </label>
                            <select
                              value={formData.paymentGateway || 'custom'}
                              onChange={(e) => setFormData(prev => ({ ...prev, paymentGateway: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            >
                              <option value="custom">Custom Payment Link</option>
                              <option value="stripe">Stripe</option>
                              <option value="paypal">PayPal</option>
                              <option value="razorpay">Razorpay</option>
                              <option value="tap">Tap Payments</option>
                              <option value="paytm">Paytm</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Payment Link
                            </label>
                            <div className="flex space-x-2">
                              <input
                                type="url"
                                value={formData.customPaymentLink || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, customPaymentLink: e.target.value }))}
                                placeholder="https://payment-gateway.com/pay/invoice-123"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              />
                              <button
                                type="button"
                                onClick={() => window.open(formData.customPaymentLink, '_blank')}
                                disabled={!formData.customPaymentLink}
                                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Test Payment Link"
                              >
                                <Globe className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Enter the direct payment link from your payment gateway
                            </p>
                          </div>
                        </div>
                      )}

                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Clients can pay online through your chosen payment gateway
                      </p>
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
                    <button
                      type="button"
                      onClick={addItem}
                      className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </button>
                  </div>

                  <div className="space-y-3">
                    {/* Column Headers */}
                    <div className="grid grid-cols-12 gap-3 items-center px-3">
                      <div className="col-span-4">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Quantity</label>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Rate</label>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Amount</label>
                      </div>
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700">Tax</label>
                      </div>
                      <div className="col-span-1"></div>
                    </div>

                    {items.map((item) => (
                      <div key={item.id} className="grid grid-cols-12 gap-3 items-center p-3 bg-gray-50 rounded-lg">
                        <div className="col-span-4">
                          <textarea
                            placeholder="Item description"
                            value={item.description}
                            onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                            rows={2}
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            placeholder="Rate"
                            value={item.rate}
                            onChange={(e) => updateItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-2">
                          <div className="px-3 py-2 bg-gray-100 rounded-lg text-right font-medium">
                            {getCurrencySymbol(formData.currency)}{item.amount.toFixed(2)}
                          </div>
                        </div>
                        <div className="col-span-1">
                          <input
                            type="checkbox"
                            checked={item.taxable}
                            onChange={(e) => updateItem(item.id, 'taxable', e.target.checked)}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            title="Taxable"
                          />
                        </div>
                        <div className="col-span-1">
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            disabled={items.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="mt-6 border-t pt-4">
                    <div className="flex justify-end">
                      <div className="w-80 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Subtotal:</span>
                          <span className="font-medium">{getCurrencySymbol(formData.currency)}{subtotal.toFixed(2)}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Discount:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">{getCurrencySymbol(formData.currency)}</span>
                            <input
                              type="number"
                              value={formData.discountAmount}
                              onChange={(e) => setFormData(prev => ({ ...prev, discountAmount: parseFloat(e.target.value) || 0 }))}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tax ({formData.taxRate}%):</span>
                          <span className="font-medium">{getCurrencySymbol(formData.currency)}{taxAmount.toFixed(2)}</span>
                        </div>
                        
                        <div className="flex justify-between text-lg font-bold border-t pt-3">
                          <span>Total:</span>
                          <span>{getCurrencySymbol(formData.currency)}{total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes and Terms */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes for the client..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      rows={4}
                    />
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms & Conditions</h3>
                    <textarea
                      value={formData.terms}
                      onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                      placeholder="Payment terms and conditions..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      rows={4}
                    />
                  </div>
                </div>

                {/* Signature Field */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Signature (optional)</h3>
                  <textarea
                    value={formData.signature}
                    onChange={e => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Type your name, company, or paste an image URL..."
                    rows={2}
                  />
                  <div className="text-xs text-gray-500 mt-1">This will appear at the bottom of the invoice. You can use text or an image URL.</div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/app/invoices')}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {invoice ? 'Update Invoice' : 'Create Invoice'}
                  </button>
                  {invoice && (
                    <button
                      type="button"
                      onClick={handleDuplicateInvoice}
                      className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="Duplicate Invoice"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Live Preview Section */}
            {showPreview && previewInvoice && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
                <div className="transform scale-50 origin-top-left" style={{ width: '200%', height: '200%' }}>
                  <InvoiceTemplate invoice={previewInvoice} showActions={false} />
                </div>
              </div>
            )}
          </div>

          {/* Quick Client Creation Modal */}
          {showClientModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Client</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Name *
                    </label>
                    <input
                      type="text"
                      value={newClientData.name}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={newClientData.email}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address *
                    </label>
                    <textarea
                      value={newClientData.address}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      rows={3}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={newClientData.phone}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VAT Number (for B2B clients)
                    </label>
                    <input
                      type="text"
                      value={newClientData.taxId}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, taxId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="VAT123456789"
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowClientModal(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateClient}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Add Client
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}